import { useState } from "react";
import { BrowserRouter, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { SessionProvider, useSession, usePermissions } from "./auth/SessionContext";
import { Alert, Field, Panel } from "./components/ui";
import { ApiKeysPage } from "./modules/ApiKeysPage";
import { AudiencesPage } from "./modules/AudiencesPage";
import { AuditLogPage } from "./modules/AuditPage";
import { ComposePage } from "./modules/ComposePage";
import { HierarchyPage } from "./modules/HierarchyPage";
import { InboxPage } from "./modules/InboxPage";
import { MessageFlagsPage } from "./modules/MessageFlagsPage";
import { PartiesPage } from "./modules/PartiesPage";
import { ReadTrackingPage } from "./modules/ReadTrackingPage";
import { StreamsPage } from "./modules/StreamsPage";
import { SubscriptionsPage } from "./modules/SubscriptionsPage";
import { TenantsPage } from "./modules/TenantsPage";
import { UsersPage } from "./modules/UsersPage";

/**
 * One console with capability-gated modules, not separate hub-admin and security-admin apps: the
 * capability model already draws that boundary, and two apps means two builds, two deploys and two
 * auth flows for a line the session context already draws (§10).
 */
const MODULES = [
  { path: "/inbox", label: "Inbox", module: "messages" },
  { path: "/compose", label: "Compose & broadcast", module: "messages" },
  { path: "/read-tracking", label: "Read tracking", module: "receipts" },
  { path: "/audiences", label: "Audiences", module: "audiences" },
  { path: "/hierarchy", label: "Hierarchy", module: "audiences" },
  { path: "/parties", label: "Parties & targets", module: "parties" },
  { path: "/streams", label: "Streams & subjects", module: "streams" },
  { path: "/message-flags", label: "Message flags", module: "messages" },
  { path: "/subscriptions", label: "Subscriptions", module: "subscriptions" },
  { path: "/api-keys", label: "API keys", module: "principals" },
  { path: "/users", label: "Users & access", module: "users" },
  { path: "/audit", label: "Audit log", module: "audit" },
  { path: "/tenants", label: "Client companies", module: "tenants" },
];

export default function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </SessionProvider>
  );
}

function Gate() {
  const { session, loading } = useSession();

  if (loading) {
    return <div className="login-wrap"><p className="muted">Loading…</p></div>;
  }

  return session ? <Shell /> : <LoginPage />;
}

function Shell() {
  const { session, signOut } = useSession();
  const { canSee } = usePermissions();

  const visible = MODULES.filter((m) => canSee(m.module));
  const home = visible[0]?.path ?? "/no-access";

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="brand">
          10-4
          <small>{session?.scope?.tenant?.name ?? "Message hub"}</small>
        </div>

        <div style={{ marginTop: 12 }}>
          {visible.map((m) => (
            <NavLink
              key={m.path}
              to={m.path}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
            >
              {m.label}
            </NavLink>
          ))}
        </div>

        <div className="sidebar-foot">
          <div>{session?.user?.name}</div>
          <div style={{ marginBottom: 8 }}>{session?.user?.email}</div>
          <button className="ghost small" onClick={signOut}>Sign out</button>
        </div>
      </nav>

      <main className="main">
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/inbox" element={<RequireModule module="messages"><InboxPage /></RequireModule>} />
          <Route path="/compose" element={<RequireModule module="messages"><ComposePage /></RequireModule>} />
          <Route path="/read-tracking" element={<RequireModule module="receipts"><ReadTrackingPage /></RequireModule>} />
          <Route path="/read-tracking/:id" element={<RequireModule module="receipts"><ReadTrackingPage /></RequireModule>} />
          <Route path="/audiences" element={<RequireModule module="audiences"><AudiencesPage /></RequireModule>} />
          <Route path="/hierarchy" element={<RequireModule module="audiences"><HierarchyPage /></RequireModule>} />
          <Route path="/parties" element={<RequireModule module="parties"><PartiesPage /></RequireModule>} />
          <Route path="/streams" element={<RequireModule module="streams"><StreamsPage /></RequireModule>} />
          <Route path="/message-flags" element={<RequireModule module="messages"><MessageFlagsPage /></RequireModule>} />
          <Route path="/subscriptions" element={<RequireModule module="subscriptions"><SubscriptionsPage /></RequireModule>} />
          <Route path="/api-keys" element={<RequireModule module="principals"><ApiKeysPage /></RequireModule>} />
          <Route path="/users" element={<RequireModule module="users"><UsersPage /></RequireModule>} />
          <Route path="/audit" element={<RequireModule module="audit"><AuditLogPage /></RequireModule>} />
          <Route path="/tenants" element={<RequireModule module="tenants"><TenantsPage /></RequireModule>} />
          <Route path="/no-access" element={<NotAuthorized />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Defensive protection for direct URL navigation. The API enforces this too — hiding a route is
 * UX convenience, never security (§10).
 */
function RequireModule({ module, children }: { module: string; children: React.ReactNode }) {
  const { canSee } = usePermissions();
  return canSee(module) ? <>{children}</> : <NotAuthorized />;
}

function NotAuthorized() {
  return (
    <Panel title="Not authorized">
      <p className="muted">
        Your account has no access to this module. Ask an administrator to grant it under
        Users &amp; access.
      </p>
    </Panel>
  );
}

function LoginPage() {
  const { signIn, error } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
    } catch {
      /* the provider already surfaced it */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: -1 }}>10-4</div>
          <div className="muted">Message hub</div>
        </div>

        <Panel>
          {error && <Alert kind="error">{error}</Alert>}
          <Field label="Email">
            <input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </Field>
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <button type="submit" style={{ width: "100%" }} disabled={busy || !email || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </Panel>
      </form>
    </div>
  );
}
